import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, MenuItem, Button, Grid, Alert, CircularProgress, Stack
} from '@mui/material';
import {
  UploadFile as UploadFileIcon,
  Description as DocIcon
} from '@mui/icons-material';
import { tcEmployeeApi } from './employeeApi';
import { resolveExtractedDocNumber } from '../../utils/extractionUtils';
import { MAX_FILE_BYTES, MAX_FILE_LABEL, oversizeMessage } from '../../utils/fileSizeLimit';
import LocalFilePreview from './LocalFilePreview';

const EMPTY_META = { documentNumber: '', issueDate: '', expiryDate: '' };

const clean = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v).trim();
  return (!s || s.toLowerCase() === 'null') ? '' : s;
};

const toIsoDate = (v) => {
  const s = clean(v);
  if (!s) return '';
  const d = new Date(s);
  return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
};

/**
 * Upload one or more files of a single document type to a client's employee. Files are OCR'd to
 * pre-fill the document number, issue and expiry dates; the user can correct them before saving.
 */
const UploadClientEmployeeDocumentsDialog = ({ open, onClose, orgId, uid, employeeName, documentTypes = [], onUploaded, api: apiProp }) => {
  // Typing-center calls by default; the portal passes its own (employeeApi.js).
  const api = apiProp || tcEmployeeApi(orgId);
  const [docType, setDocType] = useState('');
  const [files, setFiles] = useState([]);
  const [meta, setMeta] = useState(EMPTY_META);
  const [extracting, setExtracting] = useState(false);
  const [extractNote, setExtractNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setDocType('');
    setFiles([]);
    setMeta(EMPTY_META);
    setExtracting(false);
    setExtractNote('');
    setSaving(false);
    setError('');
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const extractFrom = async (picked, typeCode) => {
    if (!picked.length || !typeCode) return;
    setExtracting(true);
    setExtractNote('');
    try {
      let note = null;
      const results = await Promise.all(picked.map(async (f) => {
        try {
          const r = await api.extract(f, typeCode);
          if (r?.data?.success === false) {
            note = r.data.error || r.data.message || note;
            return {};
          }
          const data = r?.data?.data || {};
          if (data._extractionSkipped) note = data._extractionSkippedReason || 'Automatic extraction was skipped.';
          return data;
        } catch {
          note = note || 'Automatic extraction failed. Enter the details manually.';
          return {};
        }
      }));
      const pick = (key) => {
        const hit = results.find((m) => clean(m?.[key]));
        return hit ? clean(hit[key]) : '';
      };
      const number = results.map((m) => resolveExtractedDocNumber(m, typeCode)).find(Boolean) || '';
      setMeta((prev) => ({
        documentNumber: prev.documentNumber || number,
        issueDate: prev.issueDate || toIsoDate(pick('issueDate')),
        expiryDate: prev.expiryDate || toIsoDate(pick('expiryDate'))
      }));
      if (note) setExtractNote(note);
    } finally {
      setExtracting(false);
    }
  };

  const handleFiles = (e) => {
    const all = Array.from(e.target.files || []);
    e.target.value = '';
    const picked = all.filter((f) => f.size <= MAX_FILE_BYTES);
    const tooBig = all.filter((f) => f.size > MAX_FILE_BYTES);
    setError(tooBig.length ? tooBig.map(oversizeMessage).join(' ') : '');
    if (!picked.length) return;
    setFiles((prev) => [...prev, ...picked]);
    extractFrom(picked, docType);
  };

  const handleTypeChange = (e) => {
    const code = e.target.value;
    setDocType(code);
    setMeta(EMPTY_META);
    if (files.length) extractFrom(files, code);
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    setError('');
    if (!docType) { setError('Choose a document type.'); return; }
    if (!files.length) { setError('Choose at least one file.'); return; }
    setSaving(true);
    try {
      const resp = await api.upload(uid, files, docType, {
        documentNumber: meta.documentNumber.trim() || null,
        issueDate: meta.issueDate || null,
        expiryDate: meta.expiryDate || null
      });
      const result = resp?.data?.data;
      if (!result) throw new Error(resp?.data?.error || 'Upload failed');
      onUploaded(result);
      reset();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to upload documents');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DocIcon color="secondary" />
        Upload Documents{employeeName ? ` — ${employeeName}` : ''}
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField select label="Document type" value={docType} onChange={handleTypeChange} fullWidth size="small" disabled={saving}
              SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}>
              {documentTypes.length === 0 && <MenuItem value="" disabled>No document types available</MenuItem>}
              {documentTypes.map((dt) => (
                <MenuItem key={dt.code || dt.id} value={dt.code || dt.id} sx={{ fontSize: '0.85rem' }}>
                  {dt.label || dt.name || dt.code}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ p: 1.5, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Button component="label" size="small" variant="outlined" startIcon={<UploadFileIcon />} disabled={saving || extracting}>
                  Choose files
                  <input type="file" hidden multiple accept="image/*,.pdf" onChange={handleFiles} />
                </Button>
                {extracting && <CircularProgress size={18} />}
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                All files are attached together as the current version of this document type. Max {MAX_FILE_LABEL} per file.
                Expand a file to check the scan against the extracted fields.
              </Typography>
              {files.length > 0 && (
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {files.map((f, idx) => (
                    <LocalFilePreview
                      key={`${f.name}-${f.size}-${idx}`}
                      file={f}
                      defaultExpanded={files.length === 1}
                      disabled={saving}
                      onRemove={() => removeFile(idx)}
                    />
                  ))}
                </Stack>
              )}
              {extractNote && <Alert severity="warning" sx={{ mt: 1 }}>{extractNote}</Alert>}
            </Box>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField label="Document number" value={meta.documentNumber}
              onChange={(e) => setMeta((p) => ({ ...p, documentNumber: e.target.value }))} fullWidth size="small" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Issue date" type="date" value={meta.issueDate} InputLabelProps={{ shrink: true }}
              onChange={(e) => setMeta((p) => ({ ...p, issueDate: e.target.value }))} fullWidth size="small" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Expiry date" type="date" value={meta.expiryDate} InputLabelProps={{ shrink: true }}
              onChange={(e) => setMeta((p) => ({ ...p, expiryDate: e.target.value }))} fullWidth size="small" />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving || extracting}>
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadClientEmployeeDocumentsDialog;
